// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { dateSchema } from '@/utilities/date-time-schema';
import { DateTime, Interval } from 'luxon';
import { useMemo } from 'react';

const DEFAULT_REPORT_RANGE_DAYS = 31;

export const DEFAULT_CYCLE_TIME_RANGE_DAYS = 90;

interface Options {
  start?: string;
  end?: string;
  defaultRangeDays?: number;
}

interface ReportDateRange {
  interval: Interval;
  isInvalid: boolean;
}

/**
 * Parses a string value into a DateTime object. If the value is undefined or
 * invalid, the default DateTime is returned.
 */
function getDateTime(value: string | undefined, defaultDateTime: DateTime) {
  if (value === undefined) {
    return defaultDateTime;
  }

  const parsedDate = dateSchema.safeParse(value);
  return parsedDate.success ? parsedDate.data : defaultDateTime;
}

/**
 * A React hook that memoizes the report date range represented by start and end
 * date search parameters.
 */
export function useReportDateRange(options: Options): ReportDateRange {
  return useMemo(() => {
    const today = DateTime.now().startOf('day');
    const defaultStartDateTime = today.minus({
      days: options.defaultRangeDays ?? DEFAULT_REPORT_RANGE_DAYS,
    });
    const defaultEndDateTime = today.minus({ days: 1 });
    const startDateTime = getDateTime(options.start, defaultStartDateTime);
    const endDateTime = getDateTime(options.end, defaultEndDateTime);
    const isInvalid = startDateTime > endDateTime;

    return {
      interval: isInvalid
        ? defaultStartDateTime.until(defaultEndDateTime)
        : startDateTime.until(endDateTime),
      isInvalid,
    };
  }, [options.defaultRangeDays, options.end, options.start]);
}

/**
 * A React hook that memoizes an Interval object representing the time period
 * between two dates.
 */
export function useReportInterval(options: Options) {
  return useReportDateRange(options).interval;
}
